import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { renderErrorOrLoading } from "../commons/renderErrorOrLoading";
import { standingsCopy } from "../config/appCopy";
import type {
  CalculatedResultRow,
  CountryRow,
  PricepoolEntry,
} from "../data/types";
import { useData } from "../data/useData";
import { useResults } from "../data/useResults";
import { useUser } from "../data/useUser";
import { Flag } from "./Flag";

const WIDE_LAYOUT_MIN_WIDTH = 1000;
const CURRENT_USER_CELL_STYLE = { fontWeight: 700 };

export function Standings(): JSX.Element {
  const { results, error, loading } = useResults();
  const { countries, pricepool } = useData();
  const { user } = useUser();
  const wide = window.innerWidth > WIDE_LAYOUT_MIN_WIDTH;
  const errorOrLoading = renderErrorOrLoading({ loading, error });

  if (errorOrLoading) return errorOrLoading;

  return (
    <TableContainer
      component={Paper}
      data-testid="standings-scroll-container"
      sx={{ maxHeight: 420, overflow: "auto", width: "100%" }}
    >
      <Table stickyHeader aria-label="standings table">
        <TableHead>
          <TableRow>
            {wide && <TableCell>position</TableCell>}
            <TableCell>name</TableCell>
            <TableCell>points</TableCell>
            {wide && <TableCell>prize</TableCell>}
            <TableCell>teams</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {results.map((row, index) => (
            <StandingsRow
              key={`${row.name ?? "row"}-${index}`}
              countries={assignedCountries(countries, row.name)}
              isCurrentUser={user === row.name}
              participant={row}
              position={index + 1}
              prize={renderPrize(index, results.length, pricepool)}
              wide={wide}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface StandingsRowProps {
  countries: CountryRow[];
  isCurrentUser: boolean;
  participant: CalculatedResultRow;
  position: number;
  prize: string;
  wide: boolean;
}

function StandingsRow({
  countries,
  isCurrentUser,
  participant,
  position,
  prize,
  wide,
}: StandingsRowProps): JSX.Element {
  return (
    <TableRow>
      {wide && <TableCell>{position}</TableCell>}
      <TableCell
        component="th"
        scope="row"
        sx={isCurrentUser ? CURRENT_USER_CELL_STYLE : undefined}
      >
        {participant.name}
      </TableCell>
      <TableCell>{participant.score}</TableCell>
      {wide && <TableCell>{prize}</TableCell>}
      <TableCell>
        {countries.map((country) => (
          <ParticipantFlag
            key={country.code ?? `${country.name}-${participant.name}`}
            country={country}
            wide={wide}
          />
        ))}
      </TableCell>
    </TableRow>
  );
}

function ParticipantFlag({
  country,
  wide,
}: {
  country: CountryRow;
  wide: boolean;
}): JSX.Element {
  const flagWidth = wide ? 64 : 32;

  return (
    <Flag
      country={country.code}
      style={{
        borderRadius: 2,
        boxShadow:
          "0 0 0 1px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.16)",
        display: "inline-block",
        height: flagWidth / 2,
        margin: 3,
        opacity: country.eliminated ? 0.1 : 1,
        position: "relative",
        width: flagWidth,
      }}
    />
  );
}

function renderPrize(
  index: number,
  resultCount: number,
  pricepool?: PricepoolEntry[],
): string {
  const amount = pricepool?.[index]?.amount;

  if (amount) return `${amount} eur`;
  if (index === resultCount - 1) return ` ${standingsCopy.consolationPrizeFallback}`;
  return "";
}

function assignedCountries(
  countries: CountryRow[] | undefined,
  participantName: string | undefined,
): CountryRow[] {
  return (countries ?? []).filter(
    (country) => country.assignedTo === participantName,
  );
}
